import React from 'react'
import * as R from 'ramda'
import Link from 'redux-first-router-link'
import { t } from 'ttag'

import { useDispatch } from 'react-redux'
import Pane from 'components/common/Pane'
import Icon from 'components/common/Icon'
import ReactMarkdown from 'react-markdown'
import { titleCase } from 'utils/stringFormat'
import { useLanguage, useLocation } from 'hooks'
import { toPage } from 'ducks/router.js'
import markdown from './markdown/'
import Illustration from 'components/common/Illustration'
import { SentryErrorBoundary } from 'components/common/SentryErrorBoundary'
import { TabsBar } from 'components/shared/TabsBar/TabsBar'

const toHelpTab = (tab = 0) => toPage('training', tab)

const HelpTabBar = ({ current = 0, pages }) => {
  const dispatch = useDispatch()

  return (
    <TabsBar
      onClick={index => dispatch(toHelpTab(index))}
      activeTab={current}
      isScrollable
      tabs={pages.map(({ tab, label }) => ({
        id: tab,
        label: titleCase(label),
        sequenceNumber: tab + 1,
      }))}
    />
  )
}

const HelpFooter = ({ next, prev }) => (
  <div className="HelpFooter">
    {prev && (
      <span className="prev">
        <Icon name="chevronLeft" />
        <LinkToPage {...prev} />
      </span>
    )}
    {next && (
      <span className="next">
        <LinkToPage {...next} />
        <Icon name="chevronRight" />
      </span>
    )}
  </div>
)

const Prefix = ({ tab }) =>
  tab > 0 && <span className="Prefix">{t`Part ${tab}: `}</span>

const LinkToPage = ({ title, tab }) => (
  <Link to={toHelpTab(tab)}>
    <Prefix tab={tab} />
    {title}
  </Link>
)

const IndexPart = ({ tab, title, sections }) => (
  <>
    <Prefix tab={tab} />
    <Link to={toHelpTab(tab)}>{title}</Link>
    {sections.map((txt, idx) => (
      <div key={idx} className="section">
        {txt}
      </div>
    ))}
  </>
)

const HelpIndex = ({ pages }) => (
  <div className="HelpIndex">
    {pages.map(props =>
      props.tab === 0 ? null : <IndexPart key={props.tab} {...props} />,
    )}
  </div>
)

const Img = ({ node, src, ...props }) => {
  return (
    <SentryErrorBoundary>
      <Illustration
        name={src.match(/\/(?:undraw_)?([^/]*)\.(?:svg|png)$/)[1]}
        {...props}
      />
    </SentryErrorBoundary>
  )
}

const Anchor = ({ node, href, children, ...props }) =>
  href.match(/^[./]/) ? (
    <Link to={href} {...props} children={children} />
  ) : (
    <a className="external-link" href={href} children={children} {...props} />
  )

// Don't wrap images etc. in a <p> unless inline in a paragraph
const Paragraph = ({ node, children, ...props }) =>
  React.Children.toArray(children).some(child => typeof child === 'string') ? (
    <p {...props}>{children}</p>
  ) : (
    <>{children}</>
  )

const HelpPage = () => {
  const location = useLocation()
  const tab = parseInt(R.pathOr(0, ['payload', 'tab'], location))
  const language = useLanguage()
  const pages = markdown[language] || []
  const page = pages[tab]
  if (!page) return '...'
  return (
    <Pane>
      <HelpTabBar current={tab} pages={pages} />
      <div className="HelpPage">
        <h1>
          <Prefix tab={page.tab} />
          {page.title}
        </h1>
        <ReactMarkdown
          children={page.content}
          components={{ img: Img, a: Anchor, p: Paragraph }}
        />
        {tab ? null : <HelpIndex pages={pages} />}
        <HelpFooter next={pages[tab + 1]} prev={pages[tab - 1]} />
      </div>
    </Pane>
  )
}

export default HelpPage
